<template>
	<v-app id="app">
		<!-- NAVDRAWER  -->
		<v-navigation-drawer v-if="sessionActive" app floating v-model="drawer" color="white" disable-resize-watcher>
			<v-card flat>
				<v-card-title>
					<v-btn plain :to="{ name: 'Home' }">
						<v-img src="@/assets/logo.svg" height="32" contain position="left center" />
					</v-btn>
				</v-card-title>
				<v-card-text>
					<smart-nav-bar vertical />
				</v-card-text>
			</v-card>
		</v-navigation-drawer>

		<!-- APP BAR -->
		<v-app-bar app v-if="sessionActive" flat v-resize="onResize" v-scroll="onScroll" height="80" class="background">
			<v-container>
				<v-row align="center">
					<v-app-bar-nav-icon v-if="!horizontal" @click="drawer = !drawer"></v-app-bar-nav-icon>

					<v-toolbar-title>
						<v-img src="@/assets/logo.svg" height="40" contain position="left center" />
					</v-toolbar-title>

					<v-spacer />

					<smart-nav-bar v-if="horizontal" />

					<v-spacer />

					<v-badge v-if="cartItemCount" :content="cartItemCount" 
						:value="cartItemCount" class="my-auto" >
						<v-btn small icon plain class="my-auto" :to="{ name: 'ShoppingCart' }">
							<v-icon>mdi-cart-outline</v-icon>
						</v-btn>
					</v-badge>

					<v-spacer />

					<v-menu offset-y transition="slide-y-transition">
						<template #activator="{ on, attrs }">
							<v-btn v-bind="attrs" icon class="my-auto" v-on="on">
								<v-avatar color="secondary" size="40">
									<span class="white--text">{{ userInitial }}</span>
								</v-avatar>
							</v-btn>
						</template>
						<v-list dense>
							<v-list-item link :to="{ name: 'UserProfile' }">
								<v-list-item-icon class="mr-3">
									<v-icon>mdi-account-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>{{ $t("App.My_profile") }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
							<v-list-item link :to="{ name: 'Logout' }">
								<v-list-item-icon class="mr-3">
									<v-icon>mdi-exit-to-app</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title>{{ $t("App.Sign_out") }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</v-menu>
				</v-row>
			</v-container>
		</v-app-bar>

		<!-- MAIN -->
		<v-main v-resize="onResize" v-scroll="onScroll" class="background fill-height">
			<transition name="fade" mode="out-in">
				<router-view />
			</transition>
		</v-main>

		<!-- SCROLL TOP -->
		<v-btn v-if="sessionActive && this.scroller" style="position: fixed; bottom: 16px; right: 16px; z-index: 1000;" 
			color="secondary" icon v-on:click.stop="$vuetify.goTo(0)">
			<v-icon>mdi-arrow-up-thin-circle-outline</v-icon>
		</v-btn>

		<!-- BOTTOM SHEET -->
		<v-bottom-sheet v-model="bottomSheet.isVisible" hide-overlay scrollable width="auto">
			<v-sheet v-if="bottomSheet.success"><v-btn icon large @click="bottomSheet.isVisible = false"><v-icon
						color="success">mdi-thumb-up</v-icon></v-btn></v-sheet>
			<v-card v-else class="mx-auto" max-height="150px" dark>
				<v-card-title>
					<v-spacer />
					<v-btn right icon @click="bottomSheet.isVisible = false"><v-icon>mdi-close-circle-outline</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="text-center">
					{{ bottomSheet.message }}
				</v-card-text>
			</v-card>
		</v-bottom-sheet>
	</v-app>
</template>

<script>
import lodash from "lodash";
import { mapGetters } from "vuex";
import SmartNavBar from "./components/SmartNavBar.vue";
import { getSupportedLocale } from "@/plugins/i18n";

export default {
	name: "App",
	components: { SmartNavBar },
	data: () => ({
		drawer: false,
		scroller: false,
		tenantName: process.env.VUE_APP_TENANT_NAME,
		appName: process.env.VUE_APP_APP_NAME,
	}),
	watch: {
		'$root.$i18n.locale': {
			handler(newValue) {
				// validate locale before updating stores
				const validLocale = getSupportedLocale(newValue);
				// update all stores with validated locale
				this.$store.commit('session/setLocale', validLocale)
				this.$store.commit('catalog/setLocale', validLocale)
				this.$store.commit('shoppingCart/setLocale', validLocale)
				// update vuetify
				this.$vuetify.lang.current = validLocale
			},
			immediate: true,
		},
	},
	computed: {
		...mapGetters({
			sessionActive: "session/sessionActive",
			userName: "session/userName",
			bottomSheet: "session/bottomSheet",
			cartItemCount: "shoppingCart/cartItemCount",
		}),
		horizontal() {
			return this.$vuetify.breakpoint.mdAndUp;
		},
		userInitial() {
			return this.userName ? this.userName.charAt(0).toUpperCase() : "?";
		}
	},
	methods: {
		onResize() {
			let fx = lodash.debounce(this.setContentHeight, 1000);
			fx();
		},
		onScroll() {
			this.scroller = window.pageYOffset > 256;
		},
		setContentHeight() {
			this.$store.dispatch("session/updateUI", {
				contentHeight:
					window.innerHeight -
					this.$vuetify.application.top -
					this.$vuetify.application.footer,
			});
		},
	},
};
</script>

<style>
.sticky-top {
	position: sticky;
	top: 80px;
	z-index: 3;
}

/*** SCROLLBAR ***/
/* width */
::-webkit-scrollbar {
	width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
	background: #eceff1;
	/* blue-grey lighten-5  */
}

/* Handle */
::-webkit-scrollbar-thumb {
	background: #b0bec5;
	/* blue-blue-grey lighten-5  */
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
	background: #78909c;
	/* blue-grey lighten-1 */
}

/*** TRANSITIONS ***/
.fade-enter {
	opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease-out;
}

.fade-leave-to {
	opacity: 0;
}

.slide-fade-enter {
	transform: translateX(10px);
	opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
	transition: all 0.25s ease;
}

.slide-fade-leave-to {
	transform: translateX(-10px);
	opacity: 0;
}

.grad {
	background-image: radial-gradient(circle,
			#e3f2fd,
			#dbeefc,
			#d4ebfc,
			#cce7fb,
			#c5e3fb,
			#bee0fb,
			#b8dcfa,
			#b1d9fa,
			#a9d5fa,
			#a1d2f9,
			#98cef9,
			#90caf9) !important;
}

@media (min-width: 1904px) {
    .container {
        max-width: 1280px !important;
    }
}
</style>
